/* eslint-disable no-nested-ternary */
import React, { Suspense, useContext, useMemo } from "react";
import { Redirect, Route, Location } from "react-router-dom";
import { IRoute } from "./RouteInterface";
import { AuthContext } from "../auth/AuthProvider";
import { PageLoader } from "../components/uiComponents";
import { getPermissionKeyFromPath, hasPermission } from "./utils/routePermissionMapper";
import { Roles } from "@boulevard1/mystake-common-sdk";

// Unauthorized component - defined outside to avoid reference issues
const UnauthorizedComponent = React.lazy(() =>
  import("../components/pages/Unauthorized").then((module) => ({
    default: module.Unauthorized,
  })),
);

export const RouteWithSubRoutes: React.FC<IRoute> = (route) => {
  const { currentUser, loader } = useContext(AuthContext);

  // Permission check component wrapper
  const PermissionCheck: React.FC<{ location: Location; children: React.ReactNode }> = ({ location, children }) => {
    const hasAccess = useMemo(() => {
      if (!currentUser || !route.private) {
        return true; // Non-private routes or no user = no permission check
      }

      // Only check permissions for admin users in company routes
      const isAdmin = currentUser.roles?.includes(Roles.ADMIN) || currentUser.roles?.includes(Roles.SUPER_ADMIN);
      if (!isAdmin || currentUser.isInvestor) {
        return true; // Not an admin or is investor = skip permission check
      }

      // Extract companyId from current location pathname
      const pathSegments = location.pathname.split("/").filter((seg: string) => seg);
      const companyIdIndex = pathSegments.findIndex((seg: string) => seg.startsWith("companies-"));
      
      if (companyIdIndex === -1) {
        return true; // Not a company route = no permission check
      }

      const companyId = pathSegments[companyIdIndex];
      const companyRole = currentUser.rolesByCompany?.find((role) => role.companyId === companyId);
      
      if (!companyRole) {
        return true; // No role for this company = no permission check needed
      }

      // Get permission key for this route
      const permissionKey = getPermissionKeyFromPath(location.pathname);
      
      // Check if user has access
      return hasPermission(permissionKey, companyRole.permissions);
    }, [currentUser, route.private, location.pathname]);

    if (!hasAccess) {
      return (
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedComponent />
        </Suspense>
      );
    }

    return <>{children}</>;
  };

  return (
    <Suspense fallback="">
      <Route
        // eslint-disable-next-line react/destructuring-assignment
        path={route.path}
        render={(props) =>
          route.redirect ? (
            <Redirect to={route.redirect} />
          ) : route.private ? (
            loader ? (
              <PageLoader />
            ) : currentUser && !loader ? (
              <PermissionCheck location={props.location}>
                {route.container ? (
                  <route.container {...route.containerProps}>
                    {route.component && <route.component {...props} routes={route.routes} />}
                  </route.container>
                ) : (
                  route.component && <route.component {...props} routes={route.routes} />
                )}
              </PermissionCheck>
            ) : (
              <Redirect to="/signin" />
            )
          ) : route.container ? (
            <route.container {...route.containerProps}>
              {route.component && <route.component {...props} routes={route.routes} />}
            </route.container>
          ) : (
            route.component && <route.component {...props} routes={route.routes} />
          )
        }
      />
    </Suspense>
  );
};
